import {
  BulkChangeBotsStatusRequest,
  FixMeLater,
  JobEndpoint,
} from "@shared/types";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getStoreFromSliceName } from "@shared/utils/slices";

export interface BulkCancelTradesJobArgs {
  trade_ids: number[];
  ratio: number;
}
export type BulkChangeBotsStatusJobArgs = BulkChangeBotsStatusRequest;
export interface BulkCancelSignalsJobArgs {
  signal_ids: number[];
}

export enum JobToastType {
  Trade = "Trade",
  Bot = "Bot",
  Signal = "Signal",
}

export interface ActiveJobArgs<T = any> {
  args: T;
  id: number;
  type: JobToastType;
  is_active: boolean;
}

export const JOBS_SLICE_PATH = "jobs";
export interface JobsState {
  jobsData: { [key in JobEndpoint]?: Record<string, any> };
  activeJobArgs: ActiveJobArgs[];
}
const InitialState: JobsState = {
  jobsData: {},
  activeJobArgs: [],
};
export const jobsSlice = createSlice({
  name: JOBS_SLICE_PATH,
  initialState: InitialState,
  reducers: {
    setJobData(
      state,
      action: PayloadAction<{
        args: any;
        data: any;
        endpointName: JobEndpoint;
        isError?: boolean;
        errorMessage?: string;
      }>
    ) {
      const { args, data, endpointName, isError, errorMessage } =
        action.payload;
      const allEndpointData = state.jobsData[endpointName] ?? {};
      const stringArgs = JSON.stringify(args);
      allEndpointData[stringArgs] = {
        data,
        isError: isError ?? false,
        errorMessage,
      };
      state.jobsData[endpointName] = allEndpointData;
    },
    addActiveJobArgs(
      state,
      action: PayloadAction<Omit<ActiveJobArgs, "is_active">>
    ) {
      state.activeJobArgs.push({ ...action.payload, is_active: true });
    },
    setActiveJobArgInactive(state, action: PayloadAction<number>) {
      const item = state.activeJobArgs.find((i) => i.id === action.payload);
      if (item) {
        item.is_active = false;
      }
    },
    removeActiveJobArgs(state, action: PayloadAction<number>) {
      state.activeJobArgs = state.activeJobArgs.filter(
        (item) => item.id !== action.payload
      );
    },
  },
});
export const jobsActions = jobsSlice.actions;

export const selectJobsState = (state: FixMeLater): JobsState =>
  getStoreFromSliceName(state, JOBS_SLICE_PATH);

export const selectActiveJobArgsByType = (type: JobToastType) =>
  createSelector(selectJobsState, (jobsStore) =>
    jobsStore.activeJobArgs.filter((item) => item.type === type)
  );

export const selectJobsData = (endpointName: JobEndpoint, args: any) =>
  createSelector(selectJobsState, (jobsStore) => {
    const stringArgs = JSON.stringify(args);
    return jobsStore.jobsData[endpointName]?.[stringArgs] as
      | { data: any; isError: boolean; errorMessage?: string }
      | undefined;
  });
